import React, { useState, useEffect } from 'react';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import AddUserDialog from '../../components/appointments/addUserDialog';
import LoadingMask from '../../components/appointments/loadingMask';
import { useNotifications } from '../../components/appointments/notifications';


const UserManagerPage = ({ accountFromUrl, acctId }) => {
  // Initialize notifications system
  const { showSuccess, showError, NotificationComponent } = useNotifications();

  const [users, setUsers] = useState([]);
  const [openUserDialog, setOpenUserDialog] = useState(false);
  const [availableCalendars, setAvailableCalendars] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [isUserDeleteConfirmOpen, setIsUserDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null); const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCalendarsLoading, setIsCalendarsLoading] = useState(true);
  const [isUsersLoading, setIsUsersLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loggedInUserEmail, setLoggedInUserEmail] = useState('');
  const [superAdminCount, setSuperAdminCount] = useState(0);
  const [isLeaveConfirmOpen, setIsLeaveConfirmOpen] = useState(false);
  const [userToLeave, setUserToLeave] = useState(null);

  const token = localStorage.getItem('token');
  useEffect(() => {
    if (acctId) {
      fetchUsersWithBackendAcctId(acctId);
      fetchCalendarsWithBackendAcctId(acctId);
    } else {
      showError('Account ID not found for this account.');
    }
    // Get the logged-in user's email from localStorage
    const storedUserEmail = localStorage.getItem('userEmail');
    if (storedUserEmail) {
      setLoggedInUserEmail(storedUserEmail);
    }
  }, [acctId]);

  // Always use backendAcctId for fetchUsers
  const fetchUsersWithBackendAcctId = async (backendAcctId) => {
    setIsUsersLoading(true);
    try {
      if (!backendAcctId) {
        console.error('No backend account ID found for URL account:', accountFromUrl);
        setIsUsersLoading(false);
        return;
      }
      const response = await fetch('/api/ui/appointment/gCal/searchUser', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ acctId: backendAcctId }),
      });
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const responseData = await response.json();
      if (responseData.success && Array.isArray(responseData.data)) {
        setUsers(responseData.data);
        // Count Super Admin users
        const superAdmins = responseData.data.filter(user =>
          user.role === 'Super Admin' ||
          user.role === 0 ||
          user.account?.role === 'superadmin' ||
          user.account?.role === 2 ||
          user.roleLabel === 'superadmin'
        );
        setSuperAdminCount(superAdmins.length);
      } else {
        setUsers([]);
        setSuperAdminCount(0);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
      setSuperAdminCount(0);
      showError('Failed to load users. Please try again.');
    } finally {
      setIsUsersLoading(false);
    }
  };

  // Always use backendAcctId for fetchCalendars
  const fetchCalendarsWithBackendAcctId = async (backendAcctId) => {
    try {
      setIsCalendarsLoading(true);
      const userId = localStorage.getItem('userId');
      if (!backendAcctId) {
        console.error('No backend account ID found for URL account:', accountFromUrl);
        setIsCalendarsLoading(false);
        return;
      }
      const response = await fetch('/api/ui/appointment/gCal/getCalendars', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ acctId: backendAcctId, userId }),
      });
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const responseData = await response.json();
      if (responseData.success) {
        if (Array.isArray(responseData.data)) {
          setAvailableCalendars(responseData.data);
        } else if (Array.isArray(responseData.calendars)) {
          const formattedCalendars = responseData.calendars.map(cal => ({
            id: cal._id,
            name: cal.calendarName
          }));
          setAvailableCalendars(formattedCalendars);
        } else {
          setAvailableCalendars([]);
        }
      } else {
        setAvailableCalendars([]);
      }
    } catch (error) {
      console.error('Error fetching calendars:', error);
      setAvailableCalendars([]);
      showError('Failed to load calendars. Please refresh the page and try again.');
    } finally {
      setIsCalendarsLoading(false);
    }
  };


  // Function to handle user submission (always uses backendAcctId)
  const handleAddUser = async (userData) => {
    setIsLoading(true);
    try {
      // Always use acctId from props
      if (!acctId) {
        showError('Account ID not found for this account.');
        setIsLoading(false);
        return;
      }
      // Merge acctId into userData (overwriting any existing acctId)
      const payload = { ...userData, acctId };
      // Make actual API call to add user
      const response = await fetch('/api/ui/appointment/gCal/createUser', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();


      if (data.success) {
        // Refresh the users list
        fetchUsersWithBackendAcctId(acctId);
        // Show the exact success message from the API response
        showSuccess(data.message || 'User added successfully');
        // Close the dialog
        setOpenUserDialog(false);
      } else {
        // Directly show the error message from the backend
        showError(data.message || 'Failed to add user');
        return; // Exit the function early to prevent further processing
      }
    } catch (error) {
      console.error('Error adding user:', error);
      showError(error.message || 'Failed to add user. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  // Function to handle user edit
  const handleEditUser = (user) => {
    // Make sure we have a valid ID for the user (try different possible properties)
    const userId = user.id || user._id;


    let roleString;

    // Handle role based on different possible formats in the user object
    if (user.account?.role === 'superadmin' || user.account?.role === 0 ||
      user.role === 'Super Admin' || user.role === 0 ||
      user.roleLabel === 'superadmin') {
      roleString = 'Super Admin';
    } else if (user.account?.role === 'admin' || user.account?.role === 1 ||
      user.role === 'Admin' || user.role === 1 ||
      user.roleLabel === 'admin') {
      roleString = 'Admin';
    } else if (user.account?.role === 2 || user.role === 2 || user.roleLabel === 'user') {
      // Convert legacy "user" role to Admin
      roleString = 'Admin';
    } else if (typeof user.role === 'string') {
      roleString = user.role.charAt(0).toUpperCase() + user.role.slice(1);
    } else {
      roleString = user.role || 'Admin'; // Default to Admin instead of User
    }

    // Create a normalized version of the user object
    const normalizedUser = {
      ...user,
      // Make sure the ID is set correctly for the API
      id: userId,
      // Use the determined role string
      role: roleString
    };


    setCurrentUser(normalizedUser);
    setIsEditingUser(true);
    setOpenUserDialog(true);
  };

  // Function to handle user delete confirmation
  const handleDeleteUser = (user) => {

    setUserToDelete(user);
    setIsUserDeleteConfirmOpen(true);
  };

  // Function to handle leaving the account (for current Super Admin user)
  const handleLeaveAccount = () => {

    // Set userToLeave to null to indicate it's for the current user
    setUserToLeave(null);

    // Open leave confirmation dialog
    setIsLeaveConfirmOpen(true);
  };

  // Function to handle leaving for another Super Admin from the account
  const handleLeaveForOtherUser = (user) => {
    // Store the user to be removed in a state variable to access in the confirmation dialog
    setUserToLeave(user);
    // Open leave confirmation dialog
    setIsLeaveConfirmOpen(true);
  };

  // Function intentionally removed as its functionality has been integrated into confirmLeaveAccount

  // Function to confirm leaving the account
  const confirmLeaveAccount = async () => {
    setIsLoading(true);
    try {
      // Always get backendAcctId from localStorage
      const backendAcctId = localStorage.getItem('acctId') || '';
      if (!backendAcctId) {
        showError("Account ID is missing or not accessible. Cannot process leave request.");
        setIsLoading(false);
        return;
      }

      let userId;

      // If userToLeave is set, we're processing leave for another user
      if (userToLeave) {
        userId = userToLeave.id || userToLeave._id || userToLeave.userId ||
          userToLeave.account?.id || userToLeave.account?._id || userToLeave.account?.userId;
      } else {
        // Otherwise, we're processing leave for the current user
        userId = localStorage.getItem('userId');
      }

      if (!userId) {
        showError("User ID is missing. Cannot process leave request.");
        setIsLoading(false);
        return;
      }

      // Use the leaveAccount API endpoint for both cases
      const payload = {
        userId: userId,
        acctId: backendAcctId
      };


      const response = await fetch(`/api/ui/appointment/gCal/leaveAccount`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.success) {
        if (userToLeave) {
          // For another user, just show success and refresh the list
          showSuccess(data.message || `${userToLeave.name || userToLeave.email || 'User'} has successfully left the account`);
          fetchUsersWithBackendAcctId(backendAcctId);
        } else {
          // For current user, show success and redirect
          showSuccess(data.message || 'You have successfully left the account');

          // Clear account data from localStorage
          localStorage.removeItem('acctId');
          localStorage.removeItem('acctNo');
          localStorage.removeItem('name');
          localStorage.removeItem('accounts');

          // Redirect to login page or dashboard
          window.location.href = '/appointments';
        }
      } else {
        showError(data.message || 'Failed to process leave request');
      }
    } catch (error) {
      console.error('Error processing leave request:', error);
      showError(error.message || 'Failed to process leave request. Please try again.');
    } finally {
      setIsLoading(false);
      setIsLeaveConfirmOpen(false);
      setUserToLeave(null); // Reset userToLeave state
    }
  };

  // Function to cancel leaving the account
  const cancelLeaveAccount = () => {
    setIsLeaveConfirmOpen(false);
    setUserToLeave(null); // Reset userToLeave state when canceling
  };

  // Function to confirm user deletion
  const confirmUserDelete = async () => {
    if (userToDelete) {

      // Check if the ID exists, and if not, use _id as a fallback
      const userId = userToDelete.id || userToDelete._id;


      if (!userId) {
        showError("User ID is missing. Cannot delete user.");
        return;
      }

      setIsDeleting(true);
      await deleteUser(userId);
      setIsDeleting(false);
    }
  };

  // Function to cancel user deletion
  const cancelUserDelete = () => {
    setIsUserDeleteConfirmOpen(false);
    setUserToDelete(null);
  };

  // Function to delete user (always uses backendAcctId)
  const deleteUser = async (userId) => {
    if (!userId) {
      showError("Cannot delete user: Missing user ID");
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      // Always get backendAcctId from localStorage
      const backendAcctId = localStorage.getItem('acctId') || '';
      if (!backendAcctId) {
        showError('Account ID not found for this account.');
        setIsLoading(false);
        return;
      }
      // Pass both user ID and backendAcctId in the payload
      const payload = {
        id: userId,
        acctId: backendAcctId
      };
      const response = await fetch(`/api/ui/appointment/gCal/deleteUser`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (data.success) {
        fetchUsersWithBackendAcctId(backendAcctId);
        setIsUserDeleteConfirmOpen(false);
        setUserToDelete(null);
        showSuccess(data.message || 'User deleted successfully');
      } else {
        showError(data.message || 'Failed to delete user');
        return;
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      showError(error.message || 'Failed to delete user. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  // Function to handle user update (always uses backendAcctId)
  const handleUpdateUser = async (userData) => {
    setIsLoading(true);
    try {
      // Make sure we have a valid ID for the user
      const userId = userData.id || userData._id;
      if (!userId) {
        showError("Cannot update user: Missing user ID");
        setIsLoading(false);
        return;
      }
      // Always get backendAcctId from localStorage
      const backendAcctId = localStorage.getItem('acctId') || '';
      if (!backendAcctId) {
        showError('Account ID not found for this account.');
        setIsLoading(false);
        return;
      }
      // Extract only the needed fields, excluding any temporary password and duplicated role info
      const { tempPassword, adminType, ...userDataWithoutPassword } = userData;
      // Create a clean object with only the necessary fields for update
      const formattedUserData = {
        ...userDataWithoutPassword,
        // Ensure ID is explicitly set
        id: userId,
        // Make sure role is properly formatted for the backend
        // For Super Admin: role = 0, for Admin: role = 1
        role: userData.role === 'Admin' ? 1 : userData.role === 'Super Admin' ? 0 : userData.role,
        // For Super Admin: roleLabel = "superadmin", for Admin: roleLabel = "admin"
        roleLabel: userData.role === 'Admin' ? 'admin' : userData.role === 'Super Admin' ? 'superadmin' : 'admin',
        // Always use backendAcctId
        acctId: backendAcctId
      };
      // Make actual API call to update user
      const response = await fetch('/api/ui/appointment/gCal/updateUser', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formattedUserData),
      });
      const data = await response.json();
      if (data.success) {
        // Refresh the users list with backendAcctId
        fetchUsersWithBackendAcctId(backendAcctId);
        // Reset the editing state
        setIsEditingUser(false);
        setCurrentUser(null);
        // Show the exact success message from the API response
        showSuccess(data.message || 'User updated successfully');
        // Close the dialog
        setOpenUserDialog(false);
      } else {
        // Directly show the error message from the backend
        showError(data.message || 'Failed to update user');
        return;
      }
    } catch (error) {
      console.error('Error updating user:', error);
      showError(error.message || 'Failed to update user. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };



  return (
    <>
      {/* Render the NotificationComponent to display notifications */}
      <NotificationComponent />

      <div className="bg-white shadow-md rounded-lg">
        <div className="p-4 flex justify-end items-center border-b">
          <button
            onClick={() => {
              // Fetch calendars before showing the dialog
              setOpenUserDialog(true);
            }}
            className="rounded-md bg-black text-white py-2 px-4 flex items-center gap-2 hover:bg-gray-800 transition-colors"
            title="Add a new user"
          >
            <PlusIcon className="h-5 w-5" />
            Add User
          </button>
        </div>
        <div className="overflow-x-auto relative">
          {/* Skeleton placeholder for initial API loading */}
          {(isUsersLoading || isCalendarsLoading) ? (
            <table className="min-w-full divide-y divide-gray-200 animate-pulse">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Calendars</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Can Create Calendar</th>
                  <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {[...Array(3)].map((_, idx) => (
                  <tr key={idx}>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-gray-200 rounded-full"></div>
                        <div className="ml-4">
                          <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap"><div className="h-4 bg-gray-200 rounded w-32"></div></td>
                    <td className="px-4 py-4 whitespace-nowrap"><div className="h-4 bg-gray-200 rounded w-20"></div></td>
                    <td className="px-4 py-4 whitespace-nowrap"><div className="h-4 bg-gray-200 rounded w-16"></div></td>
                    <td className="px-4 py-4 whitespace-nowrap"><div className="h-4 bg-gray-200 rounded w-28"></div></td>
                    <td className="px-4 py-4 whitespace-nowrap text-center"><div className="h-5 w-5 mx-auto bg-gray-200 rounded"></div></td>
                    <td className="px-4 py-4 whitespace-nowrap text-center"><div className="h-4 bg-gray-200 rounded w-16 mx-auto"></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
          {/* Loading mask only during save/delete/submit operations */}
          {(isLoading || isDeleting || isSubmitting) && (
            <div className="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center z-10">
              <LoadingMask loading={true} />
            </div>
          )}
          {/* Real data table only when not loading */}
          {!(isUsersLoading || isCalendarsLoading) && (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phone
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Calendars
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Can Create Calendar
                  </th>
                  <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                      No users found. Add your first user to get started.
                    </td>
                  </tr>
                ) : (users.map((user) => {
                  // Use user.id, user._id, or user.email as a fallback for key
                  const userKey = user.id || user._id || user.email || Math.random();
                  return (
                    <tr key={userKey}>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center">
                            <span className="text-gray-600 font-medium">{user.name && user.name.charAt(0) ? user.name.charAt(0).toUpperCase() : '?'}</span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{user.name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{user.email}</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{user.phone || ''}</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                            ${(user.role === 'Super Admin' || user.account?.role === 'superadmin' || user.account?.role === 0) ? 'bg-green-100 text-green-800' :
                            (user.role === 'Admin' || user.account?.role === 'admin' || user.roleLabel === 'admin' || user.account?.roleLabel === 'admin' || user.account?.role === 1 || user.account?.role === 2 || user.role === 2 || user.roleLabel === 'user') ? 'bg-blue-100 text-blue-800' : 'bg-blue-100 text-blue-800'}`}>{user.account?.role ?
                              (user.account.role === 'superadmin' || user.account.role === 0 ? 'Super Admin' :
                                user.account.role === 'admin' || user.account.role === 1 || user.account.role === 2 ? 'Admin' :
                                  (typeof user.account.role === 'string' ? user.account.role.charAt(0).toUpperCase() + user.account.role.slice(1) : 'Admin')) :
                              user.roleLabel === 'admin' ? 'Admin' :
                                user.roleLabel === 'user' ? 'Admin' :
                                  user.role === 2 ? 'Admin' :
                                    user.role}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {(user.role === 'Super Admin' || user.account?.role === 'superadmin') ? (
                          <div className="text-sm text-gray-900">All Calendars</div>
                        ) : user.calendarIds && Array.isArray(user.calendarIds) && user.calendarIds.length > 0 ? (
                          <div>
                            <div className="text-sm text-gray-900 max-w-[200px]">
                              {user.calendarIds.map((calId) => {
                                const calendar = availableCalendars.find(cal => cal.id === calId || cal._id === calId);
                                const calendarName = calendar?.name || calendar?.calendarName || calId;
                                return (
                                  <div key={calId} className="truncate">{calendarName}</div>
                                );
                              })}
                            </div>
                          </div>
                        ) : user.calendarId ? (
                          <div className="text-sm text-gray-900 max-w-[200px] truncate" title={user.calendarId}>
                            {availableCalendars.find(cal => cal.id === user.calendarId || cal._id === user.calendarId)?.name ||
                              availableCalendars.find(cal => cal.id === user.calendarId || cal._id === user.calendarId)?.calendarName ||
                              user.calendarId}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-500">None</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center">
                        {(user.canCreateCalendar === true ||
                          user.role === 'Super Admin' ||
                          user.account?.role === 'superadmin') ? (
                          <div className="h-5 w-5 mx-auto bg-white border border-black rounded flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        ) : (
                          <div className="h-5 w-5 mx-auto border border-gray-300 rounded"></div>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center">
                        <div className="flex justify-center space-x-2">
                          {/* Check if user is a Super Admin (regardless of current user or not) */}
                          {(user.role === 'Super Admin' || user.role === 0 || user.account?.role === 'superadmin' ||
                            user.account?.role === 0 || user.roleLabel === 'superadmin') ? (
                            /* For Super Admin users */
                            <div className="flex items-center space-x-2">
                              {/* Edit button for Super Admin users - disabled for current user */}
                              {user.email === loggedInUserEmail ? (
                                <button
                                  className="text-gray-400 bg-gray-100 p-2 rounded-full cursor-not-allowed"
                                  title="You cannot edit your own user."
                                  disabled
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleEditUser(user)}
                                  className="text-black hover:text-gray-600 bg-gray-100 p-2 rounded-full transition-colors inline-flex"
                                  title="Edit this user"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                              )}
                              {/* Leave button for Super Admin users */}
                              {superAdminCount > 1 ? (
                                /* If there's more than one Super Admin, enable Leave/Remove button for all Super Admins */
                                <button
                                  onClick={() => {
                                    if (user.email === loggedInUserEmail) {
                                      handleLeaveAccount();
                                    } else {
                                      handleLeaveForOtherUser(user);
                                    }
                                  }}
                                  className={user.email === loggedInUserEmail
                                    ? "px-3 py-1 rounded bg-red-600 hover:bg-red-700 text-white"
                                    : "px-3 py-1 rounded bg-yellow-500 hover:bg-yellow-600 text-white"}
                                  title={user.email === loggedInUserEmail ? "Leave this account" : "Remove this Super Admin from the account"}
                                >
                                  {user.email === loggedInUserEmail ? "Leave" : "Remove"}
                                </button>
                              ) : (
                                /* If there's only one Super Admin, disable Leave button */
                                <button
                                  className="px-3 py-1 rounded bg-gray-300 text-gray-500 cursor-not-allowed"
                                  disabled={true}
                                  title="At least one Super Admin must remain in the account"
                                >
                                  Leave
                                </button>
                              )}
                            </div>
                          ) : (
                            /* For non-Super Admin users */
                            user.email !== loggedInUserEmail ? (
                              /* For other regular users, show edit/delete buttons */
                              <>
                                <button
                                  onClick={() => handleEditUser(user)}
                                  className="text-black hover:text-gray-600 bg-gray-100 p-2 rounded-full transition-colors inline-flex"
                                  title="Edit this user"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteUser(user)}
                                  className="text-red-600 hover:text-red-800 bg-gray-100 p-2 rounded-full transition-colors inline-flex"
                                  title="Delete this user"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </>
                            ) : (
                              /* For current regular users */
                              <span className="text-sm text-gray-500">Current User</span>
                            )
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                }))}
              </tbody>
            </table>
          )}
          {/* Remove duplicate real data table rendering */}
        </div>
      </div>

      <AddUserDialog
        isOpen={openUserDialog}
        onClose={() => {
          setOpenUserDialog(false);
          setIsEditingUser(false);
          setCurrentUser(null);
        }}
        onSubmit={isEditingUser ? handleUpdateUser : handleAddUser}
        initialData={currentUser}
        isEditing={isEditingUser}
        calendars={availableCalendars}
        skipEmailCheck={isEditingUser}
      />

      {isUserDeleteConfirmOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Confirm User Deletion
            </h3>

            <p className="text-sm text-gray-500 mb-4">
              Are you sure you want to delete the user "{userToDelete?.name}"? This action cannot be undone.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={cancelUserDelete}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                disabled={isDeleting}
              >
                Cancel
              </button>

              <button
                onClick={confirmUserDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center justify-center"
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                    Deleting...
                  </>
                ) : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isLeaveConfirmOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {userToLeave ? 'Confirm Remove Super Admin' : 'Confirm Leave Account'}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              {userToLeave ? (
                <>Are you sure you want to <strong>remove {userToLeave.name || userToLeave.email}</strong> from this account? They will lose access to all associated data and settings.</>
              ) : (
                <>Are you sure you want to leave this account? You will lose access to all associated data and settings.</>
              )}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={cancelLeaveAccount}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                onClick={confirmLeaveAccount}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center justify-center"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                    Processing...
                  </>
                ) : (userToLeave ? 'Remove' : 'Confirm Leave')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UserManagerPage;
